import React from 'react';
import {
  LayerTimeRangeControl,
  LayerTimeRangeControlMessage,
} from 'components/MapPage/CorporateViewTabs/LayersTab/LayerTimeRangeControl';
import type { SelectedLayerForAI } from './utils/prefetchLayerFeaturesForAI';
import {
  stripTimeFields,
  useSelectedLayersTimeRange,
} from './utils/useSelectedLayersTimeRange';

type LayersDatetimeFilterProps = {
  selectedLayers: SelectedLayerForAI[];
  filterByDatetime: boolean;
  onFilterByDatetimeChange: (enabled: boolean) => void;
  onSelectedLayersChange: (layers: SelectedLayerForAI[]) => void;
};

/**
 * Optional datetime window for Layers data sources. Stamps timeExtent onto
 * selectedLayers for AI and keeps the map Layers-tab filters aligned.
 */
const LayersDatetimeFilter: React.FC<LayersDatetimeFilterProps> = ({
  selectedLayers,
  filterByDatetime,
  onFilterByDatetimeChange,
  onSelectedLayersChange,
}) => {
  const {
    start,
    end,
    setStart,
    setEnd,
    setRangeFromSlider,
    fullStart,
    fullEnd,
    status,
    message,
    clearMapFilters,
  } = useSelectedLayersTimeRange({
    enabled: filterByDatetime,
    selectedLayers,
    onSelectedLayersChange,
  });

  return (
    <div className="dark tw:flex tw:flex-col tw:gap-2 tw:rounded-lg tw:border tw:border-border tw:bg-background tw:p-3 tw:text-foreground">
      <div className="form-check form-switch mb-0">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id="layers-filter-by-datetime"
          checked={filterByDatetime}
          onChange={(e) => {
            const enabled = e.target.checked;
            if (!enabled) {
              const hadStamp = selectedLayers.some(
                (l) => l.timeExtent || l.timeFilterLabel
              );
              if (hadStamp) {
                onSelectedLayersChange(selectedLayers.map(stripTimeFields));
              }
              clearMapFilters();
            }
            onFilterByDatetimeChange(enabled);
          }}
        />
        <label
          className="form-check-label tw:text-sm tw:text-foreground"
          htmlFor="layers-filter-by-datetime"
        >
          Filter by Datetime
        </label>
      </div>

      {filterByDatetime ? (
        status === 'loading' || status === 'idle' ? (
          <LayerTimeRangeControlMessage message="Loading layer time range…" />
        ) : status === 'unavailable' ||
          !fullStart ||
          !fullEnd ||
          !start ||
          !end ? (
          <LayerTimeRangeControlMessage
            message={
              message || 'Time info unavailable for the selected layers.'
            }
          />
        ) : (
          <LayerTimeRangeControl
            start={start}
            end={end}
            fullStart={fullStart}
            fullEnd={fullEnd}
            onStartChange={setStart}
            onEndChange={setEnd}
            onRangeFromSlider={setRangeFromSlider}
            subtitle="Applies to selected layers for Pratus AI and the map"
            idPrefix="ds-layers-time"
            showFullExtentLabels={false}
          />
        )
      ) : null}
    </div>
  );
};

export default LayersDatetimeFilter;
